import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Bug, ChevronDown, ChevronUp, RefreshCw, AlertTriangle, CheckCircle, XCircle } from "lucide-react";

const API = "/api";

/**
 * LIMITS Debug Panel
 * 
 * Displays:
 * - Hierarchy state (4H bias, 1H confirmation, 15m trigger, 5m execution)
 * - Recent redraw events with validation details
 * - Rule validation status
 */
export const LimitsDebugPanel = ({ onRedrawEvent, debugMode, onToggleDebug }) => {
  const [debugData, setDebugData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchDebugState = useCallback(async () => {
    try {
      setLoading(true);
      const [debugRes, feedRes] = await Promise.all([
        axios.get(`${API}/limits/debug`),
        axios.get(`${API}/market/feed-status`)
      ]);
      setDebugData({
        ...debugRes.data,
        feed: feedRes.data
      });
    } catch (e) {
      console.error("Error fetching debug state:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debugMode) {
      fetchDebugState();
      const interval = setInterval(fetchDebugState, 2000);
      return () => clearInterval(interval);
    }
  }, [debugMode, fetchDebugState]);

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const date = new Date(ts * 1000);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
  };

  if (!debugMode) {
    return (
      <button
        onClick={onToggleDebug}
        className="fixed bottom-4 left-4 z-50 flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-xs font-mono text-zinc-400 hover:bg-zinc-800 hover:text-zinc-300 transition-colors"
        data-testid="enable-debug-btn"
      >
        <Bug className="w-4 h-4" />
        Debug
      </button>
    );
  }

  const hierarchy = debugData?.hierarchy;
  const bias = debugData?.bias;
  const recentRedraws = debugData?.recent_redraws || [];

  const getDirectionColor = (dir) => {
    if (dir === "UP" || dir === "LONG") return "text-green-500";
    if (dir === "DOWN" || dir === "SHORT") return "text-red-500";
    return "text-zinc-500";
  };

  const getOrientationBadge = (orientation) => {
    if (orientation === "bullish") {
      return <span className="px-1.5 py-0.5 bg-green-900/50 text-green-400 text-xs rounded">BULLISH</span>;
    }
    return <span className="px-1.5 py-0.5 bg-red-900/50 text-red-400 text-xs rounded">BEARISH</span>;
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 w-96 bg-zinc-950 border border-amber-700/50 rounded-lg shadow-xl font-mono text-xs" data-testid="debug-panel">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 bg-amber-900/20 border-b border-amber-700/50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-amber-500" />
          <span className="text-amber-400 font-semibold">LIMITS DEBUG MODE</span>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={(e) => { e.stopPropagation(); fetchDebugState(); }}
            className="p-1 hover:bg-amber-900/30 rounded"
          >
            <RefreshCw className={`w-3 h-3 text-amber-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onToggleDebug(); }}
            className="p-1 hover:bg-red-900/30 rounded"
          >
            <XCircle className="w-3 h-3 text-red-500" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-amber-500" /> : <ChevronDown className="w-4 h-4 text-amber-500" />}
        </div>
      </div>

      {expanded && debugData && (
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Feed Status */}
          {debugData.feed && (
            <div className="p-3 border-b border-zinc-800 bg-zinc-900/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Data Feed</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${debugData.feed.is_simulated ? 'bg-yellow-900/50 text-yellow-400' : 'bg-green-900/50 text-green-400'}`}>
                  {debugData.feed.display_status}
                </span>
              </div>
              <div className="text-[10px] text-zinc-400">
                Source: <span className="text-zinc-300">{debugData.feed.provider_display}</span>
              </div>
              {debugData.feed.is_simulated && (
                <div className="mt-1 p-1.5 bg-yellow-900/20 border border-yellow-800/30 rounded text-[10px] text-yellow-500">
                  ⚠️ SIMULATED MODE - No live market data
                </div>
              )}
            </div>
          )}

          {/* Last Candle Close Timestamps */}
          <div className="p-3 border-b border-zinc-800">
            <div className="text-zinc-500 mb-2 uppercase tracking-wider text-[10px]">Last Candle Close (Per Timeframe)</div>
            <div className="grid grid-cols-4 gap-1">
              {["4h", "1h", "15m", "5m"].map(tf => (
                <div key={tf} className="p-1.5 bg-zinc-900 rounded text-center">
                  <div className="text-zinc-500 text-[9px]">{tf.toUpperCase()}</div>
                  <div className="text-zinc-300 text-[10px]">
                    {debugData.feed?.last_candle_close_timestamps?.[tf] 
                      ? formatTimestamp(debugData.feed.last_candle_close_timestamps[tf])
                      : "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hierarchy State Banner */}
          <div className="p-3 border-b border-zinc-800">
            <div className="text-zinc-500 mb-2 uppercase tracking-wider">Rule 6: Timeframe Hierarchy</div>
            <div className="grid grid-cols-2 gap-2">
              {/* 4H Primary */}
              <div className="p-2 bg-zinc-900 rounded border border-zinc-800">
                <div className="text-zinc-500 text-[10px]">4H PRIMARY BIAS</div>
                <div className={`font-bold ${getDirectionColor(hierarchy?.["4h_primary_bias"]?.direction)}`}>
                  {hierarchy?.["4h_primary_bias"]?.direction || "—"}
                </div>
                <div className="text-[10px] text-zinc-600 mt-1">
                  {hierarchy?.["4h_primary_bias"]?.structure?.fib_100_at 
                    ? `100% @ ${hierarchy["4h_primary_bias"].structure.fib_100_at}` 
                    : "No structure"}
                </div>
              </div>
              
              {/* 1H Confirmation */}
              <div className="p-2 bg-zinc-900 rounded border border-zinc-800">
                <div className="text-zinc-500 text-[10px]">1H CONFIRMATION</div>
                <div className={`font-bold ${getDirectionColor(hierarchy?.["1h_confirmation"]?.direction)}`}>
                  {hierarchy?.["1h_confirmation"]?.direction || "—"}
                </div>
                <div className="text-[10px] text-zinc-600 mt-1">
                  {hierarchy?.["1h_confirmation"]?.structure?.fib_100_at 
                    ? `100% @ ${hierarchy["1h_confirmation"].structure.fib_100_at}` 
                    : "No structure"}
                </div>
              </div>
              
              {/* 15m Trigger */}
              <div className="p-2 bg-zinc-900 rounded border border-zinc-800">
                <div className="text-zinc-500 text-[10px]">15M TRIGGER</div>
                <div className={`font-bold ${getDirectionColor(hierarchy?.["15m_trigger"]?.direction)}`}>
                  {hierarchy?.["15m_trigger"]?.direction || "—"}
                </div>
                <div className="text-[10px] mt-1">
                  <span className={hierarchy?.["15m_trigger"]?.trigger_enabled_direction !== "DISABLED" ? "text-green-500" : "text-red-500"}>
                    Enabled: {hierarchy?.["15m_trigger"]?.trigger_enabled_direction}
                  </span>
                </div>
              </div>
              
              {/* 5m Execution */}
              <div className="p-2 bg-zinc-900 rounded border border-zinc-800">
                <div className="text-zinc-500 text-[10px]">5M EXECUTION</div>
                <div className={`font-bold ${getDirectionColor(hierarchy?.["5m_execution"]?.direction)}`}>
                  {hierarchy?.["5m_execution"]?.direction || "—"}
                </div>
                <div className="text-[10px] text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Cannot invalidate parent
                </div>
              </div>
            </div>
            
            {/* Combined Bias */}
            <div className="mt-2 p-2 bg-zinc-900 rounded border border-zinc-800">
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Combined Bias:</span>
                <span className={`font-bold ${getDirectionColor(bias?.trade_direction_allowed)}`}>
                  {bias?.trade_direction_allowed || "NONE"}
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-zinc-500">Alignment:</span>
                <span className={bias?.bias_alignment === "ALIGNED" ? "text-green-500" : "text-yellow-500"}>
                  {bias?.bias_alignment}
                </span>
              </div>
            </div>
          </div>

          {/* Recent Redraws */}
          <div className="p-3">
            <div className="text-zinc-500 mb-2 uppercase tracking-wider">Recent Redraws (Rules 1-3)</div>
            {recentRedraws.length === 0 ? (
              <div className="text-zinc-600 italic">No redraws logged yet</div>
            ) : (
              <div className="space-y-2">
                {recentRedraws.slice().reverse().map((redraw, idx) => (
                  <div key={idx} className="p-2 bg-zinc-900 rounded border border-zinc-800">
                    {/* Rule 1: Trigger Type */}
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-amber-500 font-semibold">
                        {redraw.trigger_type === "close_beyond_618" ? "Close > 61.8%" : "Close > -23.6%"}
                      </span>
                      <span className="text-zinc-500">{redraw.timeframe?.toUpperCase()}</span>
                    </div>
                    
                    {/* Trigger Candle */}
                    <div className="text-[10px] text-zinc-400 mb-1">
                      Trigger Close: ${redraw.trigger_candle?.close_price?.toFixed(2)}
                    </div>
                    
                    {/* Rule 2: FROM/TO */}
                    <div className="grid grid-cols-2 gap-2 text-[10px]">
                      <div>
                        <span className="text-blue-400">FROM:</span>
                        <span className="text-zinc-300 ml-1">
                          {redraw.from_swing?.type?.replace("_", " ")} @ ${redraw.from_swing?.price?.toFixed(2)}
                        </span>
                      </div>
                      <div>
                        <span className="text-purple-400">TO:</span>
                        <span className="text-zinc-300 ml-1">
                          {redraw.to_extreme?.type?.replace("_", " ")} @ ${redraw.to_extreme?.price?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Rule 3: Orientation */}
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-zinc-500 text-[10px]">
                        {redraw.prior_direction?.toUpperCase()} → {redraw.new_direction?.toUpperCase()}
                      </span>
                      {getOrientationBadge(redraw.orientation)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rule Validation Status */}
          <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
            <div className="text-zinc-500 mb-2 uppercase tracking-wider">Rule Validation</div>
            <div className="space-y-1 text-[10px]">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span className="text-zinc-400">Rule 1: Redraw on candle CLOSE only</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span className="text-zinc-400">Rule 2: FROM swing, TO trigger extreme</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span className="text-zinc-400">Rule 3: Correct fib orientation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span className="text-zinc-400">Rule 4: LIMITS levels only</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span className="text-zinc-400">Rule 5: STOP beyond 61.8%</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span className="text-zinc-400">Rule 6: Lower TF cannot invalidate parent</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LimitsDebugPanel;
